<template>
    <div>
        <b-row>
            <b-col cols=6>
                <fg-input>
                    <SearchComponent v-model="store" label="Store" search-u-r-l="/store" :params="{active: true}" :id="batch.storeID" prepopulate
                        required />
                </fg-input>
            </b-col>
            <b-col cols=6>
                <fg-input>
                    <SearchComponent v-model="vendor" label="Vendor" search-u-r-l="/vendor" :id="batch.vendorID"
                        prepopulate required />
                </fg-input>
            </b-col>
            <b-col cols=6>
                <fg-input>
                    <SearchComponent v-model="program" label="Program" search-u-r-l="/Program" :id="batch.programID"
                        prepopulate clearable />
                </fg-input>
            </b-col>
            <b-col cols=6>
                <fg-input label="Order Number:" v-model="batch.purchaseOrderNumber" maxlength="80" />
            </b-col>
            <b-col v-if="hasInventoryLocations" cols="6">
                <SearchComponent :id="batch.location?.id" v-model="batch.location"
                    search-u-r-l="/inventory/inventory-locations" :prepopulate="true" label="Inventory Location"
                    clearable />
            </b-col>
            <b-col cols=6 v-if="batch.senderName">
                <fg-input label="Sender:" :value="batch.senderName" disabled />
            </b-col>
        </b-row>
        <hr>
        <!-- Batch Items Table-->
        <b-row>
            <b-col>
                <b-table striped :items="batch.items" :fields="fields" :busy="isLoading" show-empty
                    empty-text="No inventory Items has been added to this batch." :tbody-tr-class="colorCodeTable">
                    <template #table-busy>
                        <div class="text-center my-2">
                            <b-spinner class="align-middle" />
                            <strong>Loading...</strong>
                        </div>
                    </template>
                    <template #cell(packageSize)="data">
                        <span>{{ data.item.packageSize | numeral('0.00') }}</span>
                    </template>
                    <template #cell(totalShippedUnits)="data">
                        <span v-if="data.item.totalShippedUnits != null">
                            {{ data.item.totalShippedUnits | numeral('0.00') }}
                        </span>
                        <span v-else>--</span>
                    </template>
                    <template #cell(totalReceivedUnits)="data">
                        <span v-if="data.item.totalReceivedUnits != null">
                            {{ data.item.totalReceivedUnits | numeral('0.00') }}
                        </span>
                        <span v-else>--</span>
                    </template>
                    <template #cell(created)="data">
                        <span>{{ data.item.created | formatDate('lll') }}</span>
                    </template>
                    <template #cell(details)="row">
                        <a v-if="row.item.lots" href="#" size="sm" @click.prevent="row.toggleDetails">
                            <i :class="{ 'fa fa-fw': true, 'fa-chevron-right': true, 'fa-rotate-90': row.detailsShowing }"
                                aria-hidden="true" style="transition: all .2s;"></i>
                        </a>
                    </template>
                    <template #cell(drugName)="data">
                        <div>
                            <span v-if="data.item.ndc">{{ data.item.drugName }}</span>
                            <a v-else href="#" @click.prevent="setEditProduct(data.item)" v-b-tooltip.hover
                                title='There was a problem with automatic drug matching by NDC, plase select one manually clicking here.'>
                                {{ data.item.drugName }}
                            </a>
                        </div>
                        <small class="text-muted font-weight-bold">
                            {{ Drug.formatNDC542(data.item.ndc) }}
                        </small>
                        <div v-if="!itemLotsAreValid(data.item)">
                            <b-badge variant="danger">
                                <i class="fa fa-warning" />
                                Lots Incomplete
                            </b-badge>
                        </div>
                    </template>
                    <template #cell(ndc)="data">
                        <span v-if="data.item.packageID">{{ Drug.formatNDC542(data.item.ndc) }}</span>
                        <a v-else href="#" @click.prevent="setEditProduct(data.item)" v-b-tooltip.hover
                            title='There was a problem with automatic drug matching by NDC, plase select one manually clicking here.'>{{
                                Drug.formatNDC542(data.item.ndc) }}</a>
                    </template>
                    <template #cell(actions)="data">
                        <b-icon icon="x-circle-fill" class="mr-2" v-b-tooltip.hover title="Remove" variant="danger"
                            style="cursor: pointer" @click="confirmDialog('remove product', data.item)"></b-icon>
                        <b-icon icon="pencil-square" v-b-tooltip.hover title="Edit" variant="info"
                            style="cursor: pointer" @click="setEditProduct(data.item)"></b-icon>
                    </template>

                    <template #row-details="row">
                        <div v-if="row.item.lots">
                            <b-card v-for="(l, lotIndex) in row.item.lots" :key="lotIndex">
                                <strong>Lot: </strong> {{ l.lot }}
                                <br>
                                <strong>Expiration Date: </strong> {{ l.expiry }}
                                <br>
                                <strong>Quantity: </strong> {{ l.quantity }}
                                <template v-if="l.serialNumbers">
                                    <br>
                                    <strong>Serial Numbers: </strong> {{ l.serialNumbers.join(", ") }}
                                </template>
                            </b-card>
                        </div>
                    </template>
                </b-table>
            </b-col>
        </b-row>
        <div class="action-bar glass-pane">
            <b-row v-if="!isBusy">
                <b-col cols="6" class="d-flex align-items-center" style="gap: 10px;">
                    <b-button @click="addDrugModal">
                        Add Drug to Batch...
                    </b-button>
                    <b-input-group class="scan-input">
                        <b-input-group-prepend is-text>
                            <b-icon icon="upc-scan" />
                        </b-input-group-prepend>
                        <b-form-input ref="batchBarcode" v-model="batchBarcode" placeholder="Scan drug barcode"
                            aria-label="Scan drug barcode" autocomplete="off" :disabled="isLoading || isBusy"
                            @input="onBarcodeInput(false)" @keydown.enter.prevent.stop="onBarcodeEnter(false)"
                            @keydown="onBarcodeKeyDown($event, false)" />
                        <b-input-group-append v-if="isScanning" is-text>
                            <b-spinner small />
                        </b-input-group-append>
                    </b-input-group>
                    <small v-if="lastScanMessage" class="text-muted text-truncate">{{ lastScanMessage }}</small>
                </b-col>
                <b-col cols="6" class="text-right d-flex align-items-center justify-content-end" style="gap: 10px;">
                    <div v-if="!requireLots" class="d-flex flex-column align-items-start">
                        <checkbox v-model="useLotNumbers" inline class="mb-0" :has-error="lotDataDiscarded">
                            Use Lot Numbers
                        </checkbox>
                        <small v-if="lotDataDiscarded" class="text-danger">
                            Scanned lot and expiration are not being saved.
                        </small>
                    </div>
                    <b-button variant="info" :disabled="disableSave" @click="saveNewBatch">
                        Save Batch
                    </b-button>
                    <b-button variant="success" :disabled="disableSubmit" @click="confirmDialog('submit')">
                        Submit into Inventory
                    </b-button>
                    <b-button variant="danger" :disabled="disableCancel" @click="confirmDialog('cancel')">
                        Cancel Batch
                    </b-button>
                </b-col>
            </b-row>
            <div v-else class="text-center my-2">
                <b-spinner class="align-middle"></b-spinner>
                <strong>Saving...</strong>
            </div>
        </div>

        <b-modal id="inventory-batch-drug-modal" :title="(editingItem ? 'Edit' : 'Add') + ' Inventory Batch Drug'"
            :ok-title="editingItem ? 'Edit Drug' : 'Add Drug'" size="lg" @ok="handleDrugModalOk" no-fade
            @hidden="onDrugModalHidden" :ok-disabled="disableEditModalOk">

            <b-row>
                <b-col cols="12" v-if="editingItem?.id && !newItem.packageID">
                    Please select manually a drug that corresponds with: <br>
                    <strong>{{ Drug.formatNDC542(newItem.ndc) }}</strong> - <span>{{ newItem.drugName }}</span>
                </b-col>
                <b-col cols="12">
                    <b-form-group label="Scan Barcode:">
                        <b-input-group>
                            <b-input-group-prepend is-text>
                                <b-icon icon="upc-scan" />
                            </b-input-group-prepend>
                            <b-form-input v-model="modalBarcode" autofocus autocomplete="off"
                                placeholder="Scan drug barcode" @input="onBarcodeInput(true)"
                                @keydown.enter.prevent.stop="onBarcodeEnter(true)"
                                @keydown="onBarcodeKeyDown($event, true)" />
                            <b-input-group-append v-if="isScanning" is-text>
                                <b-spinner small />
                            </b-input-group-append>
                        </b-input-group>
                    </b-form-group>
                </b-col>
                <b-col cols="12">
                    <fg-input>
                        <SearchComponent v-model="drug" label="Drug" search-u-r-l="/drug"
                            :params="{ onlyActive: false, allSources: true }"
                            :params-in-get-by-id="{ drugSource: drug.source }" :id="drug.packageID" />
                    </fg-input>
                </b-col>
                <b-col cols="6">
                    <fg-input :label="shippedQuantityLabel" type="number" v-model.number="newItem.shippedQuantity" />
                </b-col>
                <b-col cols="6">
                    <div v-if="variant != 'return'">
                        <fg-input label="Packages Received:" type="number" v-model.number="newItem.receivedQuantity" />
                    </div>
                </b-col>
                <b-col v-if="hasInventoryLocations" cols="6">
                    <SearchComponent :id="newItem.location?.id" v-model="newItem.location"
                        search-u-r-l="/inventory/inventory-locations" :prepopulate="true" label="Inventory Location"
                        clearable />
                </b-col>

                <b-col v-if="effectiveUseLotNumbers && (requireLots || newItem.lots?.length)" cols="12">
                    <InventoryLotEntryComponent v-model="newItem.lots" :total-quantity="newItem.receivedQuantity"
                        :readonly="!requireLots && !scanTouchedLots && itemLotsAreValid(newItem)"
                        :activity-factor-settings="activityFactorSettings" :drug-source="drug.source"
                        :package-id="Number(drug.drugId) || 0" :fixed-factor-usages="fixedFactorUsages" />
                </b-col>
            </b-row>

        </b-modal>
    </div>
</template>
<script lang="ts">
import Vue from 'vue';
import { Component, Prop, Ref, Watch } from 'vue-property-decorator';
import Checkbox from "@/components/Inputs/Checkbox.vue";
import SearchComponent from '@/components/SearchComponent.vue';
import { InventoryBatch } from '@/models/InventoryBatch';
import { Store } from '@/models/Store';
import { Vendor } from '@/models/Vendor';
import { Drug } from '@/models/Drug/Drug';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import { Program } from '@/models/Program';
import { FixedFactorIngredientUsage, Inventory } from '@/models/Inventory';
import InventoryLotEntryComponent from './InventoryLotEntryComponent.vue';
import { InventoryLocation } from '@/models/InventoryLocation';
import moment from 'moment';
import {
    FNC1,
    Fnc1KeySequence,
    Gs1ScanFields,
    ScannedBarcodeKind,
    classifyBarcode,
    parseGs1Barcode
} from '@/util/barcodeScan';

// Scanners that are not configured to append Enter simply stop typing, so a short idle gap ends the read.
const BARCODE_IDLE_TIMEOUT = 300;

// How the idle fallback tells a wedge apart from someone typing: a scanner's keystrokes arrive with almost
// no gap between them, where a person leaves tens of milliseconds. Measured gap by gap rather than across
// the whole read, so the length of the payload cannot decide it - a long DataMatrix takes longer to arrive
// than a short NDC without being any less of a scan. Enter still submits whatever is there.
const BARCODE_MIN_SCAN_LENGTH = 8;
const BARCODE_MAX_KEYSTROKE_GAP = 50;

/** Keystroke timing for one scan field. */
interface ScanBuffer {
    timeout: number | null;
    lastKeyAt: number;
    /** Set once a gap only a person would leave has been seen; cleared when a fresh read starts. */
    handKeyed: boolean;
}
import { ACTIVITY_FACTOR_SETTINGS_OFF, ActivityFactorSettings, fetchFixedFactorIngredientUsages } from '@/util/activityFactorApi';

@Component({
    name: "InventoryBatchFormComponent",
    components: {
        SearchComponent,
        InventoryLotEntryComponent,
        Checkbox
    },
})
export default class InventoryBatchFormComponent extends Vue {

    //Batch ID
    @Prop({ default: 0 }) private value!: number;
    @Prop({ default: 'new' }) private variant!: string;
    @Prop({ default: null }) private po!: string | null;
    @Prop({ default: false }) private hasInventoryLocations!: boolean;
    @Prop({ default: false }) private requireLots!: boolean;
    @Prop({ default: false }) private requireExpirationOnReturnBatch!: boolean;
    /** Activity-factor configuration read by the page; COA entry on lot lines appears only when enabled. */
    @Prop({ default: () => ACTIVITY_FACTOR_SETTINGS_OFF }) private activityFactorSettings!: ActivityFactorSettings;

    private useLotNumbers = localStorage.getItem('ibq-use-lot-numbers') !== 'false';

    @Ref('batchBarcode') private batchBarcodeInput?: { focus?: () => void };

    private batchBarcode = "";
    private modalBarcode = "";
    private isScanning = false;
    private lastScanMessage = "";

    /**
     * Keystroke timing, held per field rather than shared. One timer between the two boxes let the first
     * keystroke in the modal cancel the batch box's pending read, stranding whatever was in it.
     */
    private scanBuffers: { batch: ScanBuffer; modal: ScanBuffer } = {
        batch: { timeout: null, lastKeyAt: 0, handKeyed: false },
        modal: { timeout: null, lastKeyAt: 0, handKeyed: false }
    };

    /** Serializes lookups so two quick reads of the same new drug cannot both miss and add two rows. */
    private scanQueue: Promise<void> = Promise.resolve();

    /**
     * Rebuilds the FNC1 separators a scanner sends as an alt code. Held per field, since a scanner
     * cannot type the control character into an input and the payload is unsplittable without it.
     */
    private batchFnc1 = new Fnc1KeySequence();
    private modalFnc1 = new Fnc1KeySequence();

    /** Keeps a scan-filled lot grid editable, so a mis-scan can still be removed. */
    private scanTouchedLots = false;

    /** Set while a scan's lot data is being dropped because lot numbers are switched off. */
    private lotDataDiscarded = false;

    /** Guards the shipped -> received mirror while a saved row is being loaded into the modal. */
    private suspendReceivedMirror = false;
    /** Formulas in which the drug being received is a flagged ingredient with a fixed factor. */
    private fixedFactorUsages: FixedFactorIngredientUsage[] = [];

    @Watch('drug')
    async onDrugChanged() {
        const drugId = Number(this.drug?.drugId);
        if (!this.activityFactorSettings.enabled || !drugId) {
            this.fixedFactorUsages = [];
            return;
        }
        this.fixedFactorUsages = await fetchFixedFactorIngredientUsages(Number(this.drug.source), drugId);
    }

    /**
     * The batch row the modal is editing, or null when it is adding a new one. Held as a reference to the
     * row itself rather than an id, because a row that has not been saved yet does not have one -- and the
     * row must stay in the batch while the modal is open so that dismissing it cannot destroy the line.
     */
    private editingItem: InventoryBatch.InventoryBatchItem | null = null;

    get effectiveUseLotNumbers(): boolean {
        return this.requireLots || this.useLotNumbers;
    }

    // Return Batches treat expiration as optional unless a client opts in via Configuration.
    // Inventory.ExpirationDate is nullable and the direct Inventory edit screen labels it "(optional)".
    get expiryRequired(): boolean {
        if (this.variant === 'return' && !this.requireExpirationOnReturnBatch) return false;
        return true;
    }

    get batchID(): number {
        return this.value;
    }

    private Drug = Drug;

    private batch: InventoryBatch = new InventoryBatch();
    private newItem: InventoryBatch.InventoryBatchItem = new InventoryBatch.InventoryBatchItem();

    private store: Store = new Store();
    private vendor: Vendor = new Vendor();
    private program: Program = new Program();
    private drug: Drug = new Drug();

    private isLoading = false;
    private isSaving = false;
    private isSubmitting = false;
    private isCancelBatch: boolean | null = null;

    created() {
        this.loadBatch();
        this.batch.purchaseOrderNumber = this.po;
    }

    get fields() {
        return [
            { key: "details", label: "", hidden: !this.batch.items.find(i => i.lots) },
            { key: "shippedQuantity", label: "Shipped Qty" },
            { key: "receivedQuantity", label: "Received Qty" },
            { key: "packageSize", label: "Package Size" },
            { key: "totalShippedUnits", label: "Units Shipped" },
            { key: "totalReceivedUnits", label: "Units Received" },
            { key: "drugName", label: "Drug", class: "drug" },
            // { key: "ndc", label: "NDC", formatter: Drug.formatNDC542 },
            { key: "location.name", label: "Location", hidden: !this.hasInventoryLocations },
            { key: "actions" },
        ].filter(f => f.hidden !== true);
    }

    get shippedQuantityLabel() {
        if (this.variant == "return") return "Units Returning:";
        return "Packages Shipped:";
    }

    @Watch('newItem.shippedQuantity')
    receivedQuantityChanged() {
        // Loading a saved row into the modal changes shippedQuantity as a side effect. Mirroring then would
        // overwrite a received count that legitimately differs, as on a partially received purchase order.
        if (this.suspendReceivedMirror) return;
        this.newItem.receivedQuantity = this.newItem.shippedQuantity;
    }

    @Watch('useLotNumbers')
    onUseLotNumbersChanged(value: boolean) {
        localStorage.setItem('ibq-use-lot-numbers', String(value));
        if (value) this.lotDataDiscarded = false;
    }

    beforeDestroy() {
        for (const buffer of [this.scanBuffers.batch, this.scanBuffers.modal]) {
            if (buffer.timeout !== null) window.clearTimeout(buffer.timeout);
        }
    }

    colorCodeTable(row: InventoryBatch.InventoryBatchItem, type: string) {
        if (!row || type !== 'row') return;

        if (!row.packageID) return 'table-danger';
    }

    async loadBatch() {
        if (!this.batchID) return;

        try {
            this.isLoading = true;

            const res = await this.$http.get<InventoryBatch>(`/Inventory/inventory-batch/${this.batchID}`);

            this.batch = new InventoryBatch(res.data);
        } catch (err) {
            console.error('Error while loading inventory batch', err);
            this.$notification(NotificationOptions.error(err));
        } finally {
            this.isLoading = false;
        }
    }

    addProduct() {
        const isReturn = this.variant == "return";
        let recQty = this.newItem.receivedQuantity;
        let shipQty = this.newItem.shippedQuantity;
        let location = this.newItem.location;
        const lots = this.newItem.lots;
        if (isReturn) {
            shipQty /= this.drug.packageSize;
            recQty /= this.drug.packageSize;
            shipQty = Math.abs(shipQty) * -1;
            recQty = Math.abs(recQty) * -1;
            lots.forEach(l => {
                l.quantity /= this.drug.packageSize;
                l.quantity = Math.abs(l.quantity) * -1;
            })
        }
        this.newItem = new InventoryBatch.InventoryBatchItem(this.drug);
        this.newItem.receivedQuantity = recQty;
        this.newItem.shippedQuantity = shipQty;
        this.newItem.location = location;
        this.newItem.lots = lots;
        this.batch.items.push(this.newItem);
        this.clearNewItemForm();
    }

    editProduct() {
        const isReturn = this.variant == "return";
        let recQty = this.newItem.receivedQuantity;
        let shipQty = this.newItem.shippedQuantity;
        const lots = this.newItem.lots;
        if (isReturn) {
            shipQty /= this.drug.packageSize;
            recQty /= this.drug.packageSize;
            shipQty = Math.abs(shipQty) * -1;
            recQty = Math.abs(recQty) * -1;
            lots.forEach(l => {
                l.quantity /= this.drug.packageSize;
                l.quantity = Math.abs(l.quantity) * -1;
            })
        }

        const item = this.editingItem;
        if (item) {
            item.applyDrug(this.drug);
            item.receivedQuantity = recQty;
            item.shippedQuantity = shipQty;
            item.location = this.newItem.location;
            item.lots = lots;
        }
        this.clearNewItemForm();
    }

    setEditProduct(item: InventoryBatch.InventoryBatchItem) {
        this.loadItemIntoNewItem(item);
        this.$bvModal.show("inventory-batch-drug-modal");
    }

    /**
     * Loads a batch row into the modal's working copy, converting return quantities back into the units the
     * form is keyed in. The row stays in the batch the whole time it is being edited, so closing the modal
     * without confirming leaves it untouched; handleDrugModalOk writes the form back onto it through
     * editProduct.
     */
    private loadItemIntoNewItem(item: InventoryBatch.InventoryBatchItem) {
        // Replacing newItem trips the shipped -> received watcher on the next tick, which would otherwise
        // flatten a received count that legitimately differs from the shipped one.
        this.suspendReceivedMirror = true;
        this.clearNewItemForm();
        this.editingItem = item;
        this.newItem = Object.assign(new InventoryBatch.InventoryBatchItem(), item);
        // Object.assign copies `lots` by reference, so the return conversion below would write its unit
        // values straight back onto the row the modal is editing -- and that row now stays in the batch for
        // the whole edit, so a dismissal would leave it corrupted, compounding on every open.
        this.newItem.lots = (item.lots ?? []).map(l => ({ ...l }));
        this.newItem.location = Object.assign(new InventoryLocation, { id: item.location?.id });
        const isReturn = this.variant == "return";
        let recQty = this.newItem.receivedQuantity;
        let shipQty = this.newItem.shippedQuantity;
        if (isReturn) {
            shipQty *= this.newItem.packageSize;
            shipQty = Math.round(Math.abs(shipQty));
            recQty *= this.newItem.packageSize;
            recQty = Math.round(Math.abs(recQty));
        }
        this.drug = Object.assign(new Drug(), {
            drugId: this.newItem.packageID,
            packageID: this.newItem.packageID,
            source: this.newItem.drugSource
        });
        this.newItem.receivedQuantity = recQty;
        this.newItem.shippedQuantity = shipQty;
        if (isReturn) {
            this.newItem.lots.forEach(l => {
                l.quantity *= this.newItem.packageSize;
                l.quantity = Math.round(Math.abs(l.quantity));
            })
        }
        this.scanTouchedLots = false;
        this.$nextTick(() => { this.suspendReceivedMirror = false; });
    }

    removeProduct(item: InventoryBatch.InventoryBatchItem) {
        this.batch.items = this.batch.items.filter(i => i.packageID != item.packageID || i.drugSource != item.drugSource);
    }

    clearNewItemForm() {
        this.drug = new Drug();
        this.newItem = new InventoryBatch.InventoryBatchItem();
        this.newItem.lots = [];
        this.newItem.location = new InventoryLocation();
        this.isCancelBatch = null;
        this.modalBarcode = "";
        this.modalFnc1.reset();
        this.scanTouchedLots = false;
    }

    confirmDialog(type: string, item: InventoryBatch.InventoryBatchItem) {
        if (type == 'submit' && this.isSubmitting) return;
        const action = type == 'remove product' ? 'remove the product from' : type;
        this.$bvModal.msgBoxConfirm(`Are you sure you want to ${action} this purchase order?`, {
            title: 'Confirm',
            size: 'sm',
            buttonSize: 'sm',
            okVariant: type == 'cancel' ? 'danger' : 'info',
            okTitle: 'YES',
            cancelTitle: 'NO',
            footerClass: 'p-2',
            hideHeaderClose: false,
            centered: true,
        })
            .then(value => {
                if (!value) return;
                if (type == 'submit') this.submitBatch();
                if (type == 'cancel') this.cancelBatch();
                if (type == 'remove product') this.removeProduct(item);
            })
            .catch(err => {
                console.error("Error caught on Clear button.", err);
            });
    }

    autoFillSingleLotQuantities() {
        if (!this.effectiveUseLotNumbers) return;
        for (const item of this.batch.items) {
            if (item.lots?.length === 1 && !item.lots[0].quantity) {
                item.lots[0].quantity = item.receivedQuantity;
            }
        }
    }

    async submitBatch() {
        if (this.isSubmitting) return;
        this.autoFillSingleLotQuantities();
        this.isSubmitting = true;
        try {
            let batch: InventoryBatch;
            try {
                batch = await this.saveNewBatchPromise;
            } catch (err) {
                // saveNewBatchPromise already notifies the user on failure
                console.error('Error during inventory batch save before submit', err);
                return;
            }
            if (!batch?.id) return;

            const ids = batch.items.map(item => item.id);
            const res = await this.$http.post<Array<Inventory>>(`/Inventory/inventory-batch-submit`, ids, { params: { batchId: this.batch.id, useLotNumbers: this.effectiveUseLotNumbers } });

            if (res.data?.length >= 0) {
                this.$notification(
                    NotificationOptions.notificationOptionsPreset("Batch items successfully submitted"
                        , NotificationOptions.NotificationTypes.success,
                    ));
                this.clearForm();
                this.$emit("batch-saved");
            } else {
                this.$notification(
                    NotificationOptions.notificationOptionsPreset("Selected batch items were submitted, but no Inventories were updated"
                        , NotificationOptions.NotificationTypes.warning,
                    ));
            }
        } catch (err: any) {
            this.$notification(NotificationOptions.errorSaveNotificationPreset("Inventory Batch Item", err));
            console.error('Error while submitting Inventory Batch Item', { err, response: err?.response });
        } finally {
            this.isSubmitting = false;
        }
    }

    async cancelBatch() {
        // The save endpoint inserts when it is given no id, so an unsaved batch must never reach it as a cancel:
        // it would add a new, uncanceled batch to the queue instead of removing anything.
        if (!this.batch.id) return;

        try {
            this.isCancelBatch = true;
            const res = await this.saveNewBatchPromise;
            if (!res?.id) return;
            this.clearForm();
            this.$emit("batch-saved");
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        } finally {
            // A cancel that fails must not leave the flag set: the next Save would post to the cancel endpoint
            // and remove the batch instead of saving it.
            this.isCancelBatch = null;
        }
    }

    get entityName(): string {
        return this.variant == 'return' ? "Return Order" : "Purchase Order";
    }

    async saveNewBatch() {
        try {
            const res = await this.saveNewBatchPromise;
            if (!res?.id) return;
            this.$notification(NotificationOptions.successSaveNotificationPreset(this.entityName));
            this.clearForm();
            this.$emit("batch-saved");
        } catch (err) {
            console.log(err);
            this.$notification(NotificationOptions.error(err));
        }
    }

    clearForm() {
        this.batch = new InventoryBatch();

        this.store = new Store();
        this.vendor = new Vendor();
        this.program = new Program();

        this.clearNewItemForm();
    }

    get saveNewBatchPromise() {
        return new Promise<InventoryBatch>((resolve, reject) => {
            // Canceling removes a batch that already exists, so it must not be held to new-batch validation.
            // A batch imported without a vendor would otherwise be impossible to clear from the queue.
            if (!this.isCancelBatch && !this.isNewBatchItemValid) {
                reject('Invalid new batch');
                return;
            }

            // Save and submit require both, but cancel does not, so fall back to what the batch already carries.
            // Number(undefined) is NaN, which serializes to null and would blank out the store of a batch being
            // canceled before its search field finished prepopulating -- StoreID is NOT NULL.
            this.batch.vendorID = Number(this.vendor?.id) || this.batch.vendorID || null;
            this.batch.storeID = Number(this.store?.id) || this.batch.storeID;
            if (Number(this.program.id) != 0) {
                this.batch.programID = Number(this.program.id);
            }
            this.batch.isReturn = this.variant == "return";

            this.isSaving = true;
            this.$http.post<InventoryBatch>(`/Inventory/inventory-batch/${this.isCancelBatch ? 'true' : ''}`, this.batch)
                .then(res => {
                    if (Number(res.data.id) >= 0) {
                        resolve(new InventoryBatch(res.data));
                    } else {
                        this.$notification(NotificationOptions.errorSaveNotificationPreset("Purchase Order"));
                        reject('No new inventory batch created.');
                    }
                })
                .catch(err => {
                    const errorMsg = 'Error while saving new Inventory Batch Item';
                    this.$notification(NotificationOptions.errorSaveNotificationPreset("Inventory Batch Item", err));
                    console.error(errorMsg, { err, response: err?.response });
                    reject(errorMsg);
                })
                .finally(() => {
                    this.isSaving = false;
                });
        });
    }

    get disableSubmit(): boolean {
        if (this.isSubmitting) return true;
        if (!this.vendor?.id) return true;
        if (this.batch.items.some(i => !i.packageID)) return true;

        // Validate lots for any item that has them when useLotNumbers is on
        if (this.effectiveUseLotNumbers) {
            const lotsIncompleteItem = this.batch.items.find(i => !this.itemLotsAreValid(i));
            if (lotsIncompleteItem) return true;
        }

        return false;
    }

    get isNewBatchItemValid(): boolean {
        return !!(this.store?.id && this.vendor?.id && this.batch.items?.length > 0);
    }

    get disableSave() {
        return !this.isNewBatchItemValid || this.isSaving;
    }

    // Canceling deliberately does not require a vendor, so this only blocks a second request while one is in
    // flight, blocks canceling a batch whose details are still loading -- the save posts the item list and
    // deletes anything missing from it, so it must not run against a half-populated form -- and blocks canceling
    // a batch that was never saved, which the save endpoint would insert rather than cancel.
    get disableCancel(): boolean {
        return !this.batch.id || this.isSaving || this.isSubmitting || this.isLoading;
    }

    get isProductFormValid(): boolean {
        return !!(this.newItem.shippedQuantity && this.drug.drugId);
    }

    /**
     * Another row already holds the drug the modal is pointed at. The row under edit is excluded: it stays in
     * the batch while the modal is open, so counting it would read as a duplicate of itself.
     */
    private duplicatesAnotherRow(): boolean {
        const editing = this.editingItem;
        const drugId = Number(this.drug.drugId);
        return this.batch.items.some(i => i !== editing && i.packageID == drugId && i.drugSource == this.drug.source);
    }

    get isBusy(): boolean {
        return this.isSaving || this.isSubmitting;
    }

    addDrugModal() {
        this.clearNewItemForm();
        this.editingItem = null;
        this.$bvModal.show("inventory-batch-drug-modal");
    }

    handleDrugModalOk() {
        if (this.editingItem) {
            this.editProduct();
        } else {
            this.addProduct();
        }
    }

    private scanBuffer(inModal: boolean): ScanBuffer {
        return inModal ? this.scanBuffers.modal : this.scanBuffers.batch;
    }

    onBarcodeInput(inModal: boolean) {
        const buffer = this.scanBuffer(inModal);
        const now = Date.now();

        if (buffer.timeout === null) {
            buffer.handKeyed = false;
        } else {
            window.clearTimeout(buffer.timeout);
            if (now - buffer.lastKeyAt > BARCODE_MAX_KEYSTROKE_GAP) buffer.handKeyed = true;
        }

        buffer.lastKeyAt = now;
        buffer.timeout = window.setTimeout(() => {
            buffer.timeout = null;
            if (this.looksLikeScannerBurst(inModal)) this.submitScan(inModal);
        }, BARCODE_IDLE_TIMEOUT);
    }

    onBarcodeEnter(inModal: boolean) {
        const buffer = this.scanBuffer(inModal);
        if (buffer.timeout !== null) window.clearTimeout(buffer.timeout);
        buffer.timeout = null;
        this.submitScan(inModal);
    }

    /**
     * Rebuilds a field separator the scanner sent as an alt code, which is the only way it can get a
     * control character into a text field. Without this the payload arrives with its fields run
     * together and the lot, being variable length, swallows everything encoded after it.
     *
     * The alt-held keys are swallowed so the browser cannot deposit the alt code's digits as text.
     * Inserting a separator that also arrived on its own is harmless - the parser skips an empty field.
     */
    onBarcodeKeyDown(event: KeyboardEvent, inModal: boolean) {
        if (!event.altKey) return;

        event.preventDefault();

        if (!(inModal ? this.modalFnc1 : this.batchFnc1).accept(event.key, event.altKey)) return;

        if (inModal) this.modalBarcode += FNC1; else this.batchBarcode += FNC1;

        // Counts as scan activity, so the burst timing still sees one uninterrupted read.
        this.onBarcodeInput(inModal);
    }

    /**
     * Whether the field filled up fast enough to have come from a scanner. The idle fallback exists for
     * scanners with no Enter suffix, but it cannot otherwise tell them from someone keying a code by hand:
     * pausing to think part way through an NDC would submit the fragment, fail to match it, and clear what
     * had been typed. Enter remains the way to submit anything typed deliberately.
     */
    private looksLikeScannerBurst(inModal: boolean): boolean {
        const value = (inModal ? this.modalBarcode : this.batchBarcode).trim();
        if (value.length < BARCODE_MIN_SCAN_LENGTH) return false;
        return !this.scanBuffer(inModal).handKeyed;
    }

    private submitScan(inModal: boolean) {
        const barcode = (inModal ? this.modalBarcode : this.batchBarcode).trim();
        if (!barcode) return;

        // Cleared on dispatch rather than on response, so the next read starts from an empty field.
        if (inModal) this.modalBarcode = ""; else this.batchBarcode = "";
        (inModal ? this.modalFnc1 : this.batchFnc1).reset();

        this.scanQueue = this.scanQueue
            .then(() => inModal ? this.processModalScan(barcode) : this.processBatchScan(barcode))
            .catch(err => console.error('Error while processing scanned barcode', err));
    }

    /**
     * Releases the edit target however the modal was closed -- confirmed, cancelled, or dismissed. The row
     * itself is never touched here: it stayed in the batch throughout, so a dismissal is a no-op on the data.
     */
    onDrugModalHidden() {
        this.editingItem = null;
        this.$nextTick(() => this.batchBarcodeInput?.focus?.());
    }

    /**
     * Resolves a scanned barcode to its drug, lot and expiration. Null when nothing matched.
     *
     * The GS1 payload is read here rather than server side so that this and the filling page read a
     * carton the same way - same parser, same field semantics - instead of keeping two
     * implementations honest by hand. The lookup is then asked only for a drug, by a plain code.
     */
    private async lookupBarcode(barcode: string): Promise<InventoryBatch.InventoryBatchBarcodeScan | null> {
        let gs1: Gs1ScanFields | undefined;

        if (classifyBarcode(barcode) === ScannedBarcodeKind.Gs1) {
            try {
                gs1 = parseGs1Barcode(barcode);
            } catch (err) {
                // Nothing in it the parser recognised. Not necessarily a bad read - an unfamiliar
                // symbology reaches this branch too - so still try the scan as a plain code below.
                console.warn('Failed to process GS1 barcode', err);
            }
        }

        // A GS1 payload identifies the drug by its GTIN. Anything else is looked up as scanned, which
        // also covers a bare GTIN-14 and a payload that carried no readable AI 01.
        const code = gs1?.gtin ?? barcode;

        try {
            const res = await this.$http.get<InventoryBatch.InventoryBatchBarcodeLookup>(
                '/Inventory/inventory-batch/barcode', { params: { barcode: code } });

            if (!res.data?.packageId) return null;

            return {
                packageId: res.data.packageId,
                drugSource: res.data.drugSource,
                packageSize: res.data.packageSize,
                lotNumber: gs1?.lotNumber ?? null,
                // The lot grid binds this into an <input type="date">, which takes no other format.
                // Formatted from the local date parts, since the parser builds a local midnight.
                expirationDate: gs1?.expirationDate
                    ? moment(gs1.expirationDate).format('YYYY-MM-DD')
                    : null
            };
        } catch (err: any) {
            // Nothing matching the scan is an ordinary outcome, not a failure worth an error toast.
            if (err?.response?.status === 404) return null;
            throw err;
        }
    }

    /** The barcode lookup answers with ids; a batch row also needs the name, NDC and package size. */
    private async loadScannedDrug(scan: InventoryBatch.InventoryBatchBarcodeScan): Promise<Drug | null> {
        try {
            const res = await this.$http.get<Drug>(`/drug/${scan.packageId}`,
                { params: { drugSource: scan.drugSource } });
            const drug = Object.assign(new Drug(), res.data, {
                drugId: Number(scan.packageId),
                packageID: Number(scan.packageId),
                source: scan.drugSource
            });
            if (!drug.packageSize) drug.packageSize = Number(scan.packageSize) || drug.packageSize;
            return drug;
        } catch (err) {
            console.error('Error while loading scanned drug', err);
            this.$notification(NotificationOptions.error(err));
            return null;
        }
    }

    /** The row a scan belongs to: same drug, preferring one that already carries the scanned lot. */
    private findScanTarget(scan: InventoryBatch.InventoryBatchBarcodeScan) {
        const matches = this.batch.items.filter(i =>
            Number(i.packageID) === Number(scan.packageId) && i.drugSource === scan.drugSource);
        if (matches.length < 2) return matches[0];
        return matches.find(i => i.lots?.some(l =>
            InventoryBatch.lotKey(l.lot) === InventoryBatch.lotKey(scan.lotNumber))) ?? matches[0];
    }

    /** Flags that a scan's lot data is being thrown away because lot numbers are switched off. */
    private noteDiscardedLotData(scan: InventoryBatch.InventoryBatchBarcodeScan) {
        if (this.effectiveUseLotNumbers) return;
        if (!scan.lotNumber && !scan.expirationDate) return;
        this.lotDataDiscarded = true;
    }

    /**
     * Records one scanned package on an item. A repeat read of a lot bumps that lot's quantity instead of
     * adding a second row.
     *
     * The received count is only re-derived from the lots while the scanner is the only thing maintaining it.
     * A count that arrived on an imported purchase order, or that someone keyed, is left exactly as it stands
     * and the scans simply build lots up towards it — the row reads as incomplete until they agree.
     *
     * `quantityStep` is what one package is worth in the representation the item is currently held in: one row
     * unit on the batch, negative on a return, or packageSize units while the return modal is holding it.
     */
    private applyScanToItem(item: InventoryBatch.InventoryBatchItem,
                            scan: InventoryBatch.InventoryBatchBarcodeScan,
                            quantityStep: number): boolean {
        const previousLotTotal = InventoryBatch.lotTotalQuantity(item.lots);
        const scannerOwnsCount = Number(item.receivedQuantity || 0) === previousLotTotal;
        const shippedFollowsReceived = scannerOwnsCount
            && Number(item.shippedQuantity || 0) === Number(item.receivedQuantity || 0);

        if (this.effectiveUseLotNumbers) {
            // A lot-tracked line can only count what it can attribute to a lot. Counting a lotless read
            // would push Received past the lot total, which blocks Submit and - because the scanner only
            // maintains a count it still agrees with - stops any later scan from re-deriving it. The drug
            // stays on the line so the operator can key the lot; the package simply is not counted.
            if (!scan.lotNumber) return false;

            item.lots = InventoryBatch.mergeScannedLot(item.lots, scan, quantityStep);
            // itemLotsAreValid requires the lots to add up to the received count exactly.
            if (scannerOwnsCount) item.receivedQuantity = InventoryBatch.lotTotalQuantity(item.lots);
        } else if (scannerOwnsCount) {
            item.receivedQuantity = Number(item.receivedQuantity || 0) + quantityStep;
        }

        if (shippedFollowsReceived) item.shippedQuantity = item.receivedQuantity;
        return true;
    }

    /** A scan that resolved a drug but carried no lot, on a batch that records lots. */
    private notifyLotlessScan() {
        this.$notification(NotificationOptions.warning(
            "That barcode carried no lot number, so the package was not counted. Scan the drug's GS1 "
            + "DataMatrix, or enter the lot and quantity by hand.",
            "No Lot Number"));
    }

    private async processBatchScan(barcode: string) {
        if (this.isLoading || this.isBusy) return;

        this.isScanning = true;
        try {
            const scan = await this.lookupBarcode(barcode);
            if (!scan) {
                this.lastScanMessage = "";
                this.$notification(NotificationOptions.warning(
                    'No drug matched the scanned barcode. Use "Add Drug to Batch..." to enter it manually.',
                    "Barcode Not Recognized"));
                return;
            }

            this.noteDiscardedLotData(scan);

            let item = this.findScanTarget(scan);
            if (!item) {
                const drug = await this.loadScannedDrug(scan);
                // loadScannedDrug has already notified; never push a row without a drug behind it.
                if (!drug) return;
                // Built from the drug so every column the table reads is an own property before Vue observes it.
                item = new InventoryBatch.InventoryBatchItem(drug);
                this.batch.items.push(item);
            }

            // Batch rows hold package counts, negated on a return, so a scanned package is worth one row unit
            // here — none of addProduct's unit conversion applies on this path.
            if (!this.applyScanToItem(item, scan, this.variant == "return" ? -1 : 1)) {
                this.lastScanMessage = `${item.drugName} - no lot number on the barcode, not counted`;
                this.notifyLotlessScan();
                return;
            }

            const lotText = this.effectiveUseLotNumbers && scan.lotNumber ? ` - lot ${scan.lotNumber}` : "";
            this.lastScanMessage = `${item.drugName}${lotText} (received ${item.receivedQuantity})`;
        } catch (err) {
            console.error('Error while looking up scanned barcode', err);
            this.$notification(NotificationOptions.error(err));
        } finally {
            this.isScanning = false;
        }
    }

    /**
     * What one scanned package is worth in the modal's fields. The return form is keyed in dispensing units,
     * since addProduct divides by packageSize on the way out; every other form counts packages.
     *
     * The size comes off the scan rather than off the selected drug. Loading an existing row into the modal
     * rebuilds `drug` from ids alone, and SearchComponent only refills the rest of it on a later tick - so
     * reading it here would silently yield a step of 1 dispensing unit and persist a fraction of a package.
     */
    private modalScanStep(scan: InventoryBatch.InventoryBatchBarcodeScan): number {
        if (this.variant != "return") return 1;
        return Number(scan.packageSize) || Number(this.drug?.packageSize) || 1;
    }

    private async processModalScan(barcode: string) {
        this.isScanning = true;
        try {
            const scan = await this.lookupBarcode(barcode);
            if (!scan) {
                this.$notification(NotificationOptions.warning(
                    "No drug matched the scanned barcode.", "Barcode Not Recognized"));
                return;
            }

            const isCurrentDrug = Number(this.drug?.drugId) === Number(scan.packageId)
                && this.drug?.source === scan.drugSource;

            if (this.drug?.drugId && !isCurrentDrug) {
                // Switching would attach this drug's lots and quantities to the one already on screen, so
                // refuse rather than corrupt them. The batch form is the surface built for scanning in bulk.
                this.$notification(NotificationOptions.warning(
                    "That barcode is for a different drug. Add or cancel this one first, then scan again.",
                    "Different Drug Scanned"));
                return;
            }

            if (!this.drug?.drugId) {
                // Nothing chosen yet. If the batch already holds a row for this drug, edit that row instead of
                // adding a second one — which is also what keeps the repeat check, which skips the row under
                // edit, from locking the OK button.
                const existing = this.findScanTarget(scan);
                if (existing) {
                    this.loadItemIntoNewItem(existing);
                } else {
                    const drug = await this.loadScannedDrug(scan);
                    if (!drug) return;
                    this.drug = drug;
                }
            }

            this.noteDiscardedLotData(scan);

            if (!this.applyScanToItem(this.newItem, scan, this.modalScanStep(scan))) {
                this.notifyLotlessScan();
                return;
            }

            this.scanTouchedLots = true;
        } catch (err) {
            console.error('Error while looking up scanned barcode', err);
            this.$notification(NotificationOptions.error(err));
        } finally {
            this.isScanning = false;
        }
    }

    itemLotsAreValid(item: InventoryBatch.InventoryBatchItem) {
        if (!this.effectiveUseLotNumbers) return true;
        // When requireLots is false, items without lots are valid
        if (!this.requireLots && (!item.lots || item.lots.length === 0)) return true;
        // When requireLots is true, items must have lots
        if (this.requireLots && (!item.lots || item.lots.length === 0)) return false;
        // Single lot without quantity is valid — will be auto-filled on submit
        if (item.lots?.length === 1 && !item.lots[0].quantity) {
            return !!item.lots[0].lot?.trim() && (!this.expiryRequired || !!item.lots[0].expiry);
        }
        // Validate all lots have required fields and quantities
        const missingItem = item.lots?.find(l => !l.lot?.trim() || (this.expiryRequired && !l.expiry) || !l.quantity);
        if (missingItem) return false;
        const lotTotalQty = item.lots?.reduce((p, i) => Number(i.quantity) + p, 0);
        return lotTotalQty == item.receivedQuantity;
    }

    get disableEditModalOk(): boolean {
        if (!this.itemLotsAreValid(this.newItem)) return true;
        if (!this.isProductFormValid) return true;
        // Grandfathered: a saved row may already share its drug with another saved row on an imported batch,
        // so the repeat check only guards rows the user is still creating. Blocking the rest would put them
        // back in the dead end of an OK button that can never be pressed.
        return !this.editingItem?.id && this.duplicatesAnotherRow();
    }
}
</script>

<style scoped lang="scss">
::v-deep(.drug) {
    width: 300px;
}

.action-bar {
    position: sticky;
    bottom: 0;
    margin-left: -15px;
    margin-right: -15px;
    padding: 0 10px;
}

.scan-input {
    max-width: 320px;
}
</style>
